/** Estados compartidos de la interfaz: carga, error y vacio. */

import { AlertCircle, Inbox } from "lucide-react";
import type { ReactNode } from "react";

import { Marca } from "./marca";
import { Button } from "./ui";

export function LoadingState({ label = "Cargando..." }: { label?: string }): ReactNode {
  return (
    <div className="flex items-center gap-3 py-10 text-muted" role="status" aria-live="polite">
      <span
        className="size-4 animate-spin rounded-full border-2 border-[var(--border-subtle)] border-t-brand-600"
        aria-hidden="true"
      />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/** Esqueleto para listas que tardan en llegar. */
export function SkeletonList({ rows = 3 }: { rows?: number }): ReactNode {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className="h-16 animate-pulse rounded-lg bg-[var(--surface-muted)]"
        />
      ))}
    </div>
  );
}

export function ErrorState({
  title = "No pudimos cargar esta seccion",
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}): ReactNode {
  return (
    <div
      className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">{title}</p>
          <p className="mt-1 text-sm opacity-90">{message}</p>
          {onRetry ? (
            <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
              Reintentar
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="rounded-xl border border-dashed border-[var(--border-subtle)] p-8 text-center">
      <Inbox className="mx-auto size-6 text-muted" aria-hidden="true" />
      <p className="mt-3 font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted">{message}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/**
 * Un estado que ocupa la pantalla entera.
 *
 * Es para cuando no hay nada mas que mostrar: la pagina no existe, el negocio
 * que pedia la URL no esta. Antes esas pantallas eran un titulo y un parrafo
 * sueltos en el medio de la nada — sin marca, sin salida y sin decir que hacer
 * despues —, asi que quien llegaba ahi cerraba la pestaña.
 *
 * Siempre lleva la marca arriba y al menos una accion: de una pantalla de
 * error se tiene que poder salir.
 */
export function PageState({
  icon,
  title,
  message,
  detail,
  actions,
  footer,
}: {
  /** Simbolo grande, decorativo: el texto ya explica que paso. */
  icon?: ReactNode;
  title: string;
  message: ReactNode;
  /** Dato concreto de lo que se pidio, por ejemplo la direccion que no existe. */
  detail?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
}): ReactNode {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-4 py-16">
      <div className="w-full max-w-md text-center">
        <Marca size="md" className="mb-10" />

        {icon ? (
          <span
            className="mx-auto mb-6 flex size-16 items-center justify-center rounded-2xl bg-[var(--surface-muted)] text-muted"
            aria-hidden="true"
          >
            {icon}
          </span>
        ) : null}

        <h1 className="titulo text-2xl font-semibold tracking-tight text-balance">{title}</h1>
        <p className="mt-3 leading-relaxed text-muted text-pretty">{message}</p>

        {detail ? (
          <p className="mt-5 truncate rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 font-mono text-sm text-muted">
            {detail}
          </p>
        ) : null}

        {actions ? (
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">{actions}</div>
        ) : null}

        {footer ? (
          <div className="mt-10 border-t border-[var(--border-subtle)] pt-6 text-sm text-muted">
            {footer}
          </div>
        ) : null}
      </div>
    </main>
  );
}
